import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Send, Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'sonner';

const UsuarioSuporte = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mensagens, setMensagens] = useState([]);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchMensagens();
    // Poll for new messages every 10 seconds
    const interval = setInterval(fetchMensagens, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchMensagens = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/mensagens-suporte`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMensagens(data);
      } else {
        toast.error('Erro ao carregar mensagens');
      }
    } catch (error) {
      console.error('Erro ao buscar mensagens:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error('Apenas arquivos PNG ou JPG são permitidos');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 2MB');
      return;
    }

    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleEnviarMensagem = async (e) => {
    e.preventDefault();
    
    if (!novaMensagem.trim()) {
      toast.error('Por favor, digite uma mensagem');
      return;
    }

    setEnviando(true);
    try {
      const token = localStorage.getItem('token');
      let screenshotUrl = null;

      // Upload screenshot if selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);

        const uploadResponse = await fetch(`${backendUrl}/api/mensagens-suporte/upload-screenshot`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          screenshotUrl = uploadData.screenshotUrl;
        } else {
          toast.error('Erro ao fazer upload da imagem');
          setEnviando(false);
          return;
        }
      }

      // Send message with screenshot URL
      const response = await fetch(`${backendUrl}/api/mensagens-suporte`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          mensagem: novaMensagem,
          screenshotUrl: screenshotUrl
        })
      });

      if (response.ok) {
        toast.success('Mensagem enviada com sucesso');
        setNovaMensagem('');
        setSelectedFile(null);
        setPreviewUrl(null);
        fetchMensagens();
      } else {
        toast.error('Erro ao enviar mensagem');
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem');
    } finally {
      setEnviando(false);
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setImageModalOpen(true);
  };

  const formatarData = (dataString) => {
    const data = new Date(dataString);
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-600">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/usuario')}
            className="mb-4 border-gray-300 text-gray-700 hover:bg-gray-100"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar ao Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Suporte</h1>
          <p className="text-gray-600 mt-2">Envie suas dúvidas e receba respostas da equipe</p>
        </div>

        {/* Messages List */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Histórico de Mensagens</CardTitle>
          </CardHeader>
          <CardContent>
            {mensagens.length === 0 ? (
              <p className="text-center text-gray-500 py-8">
                Nenhuma mensagem ainda. Envie sua primeira mensagem abaixo.
              </p>
            ) : (
              <div className="space-y-4">
                {mensagens.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-4 rounded-lg ${
                      msg.respondidoPorAdmin
                        ? 'bg-gray-100 border-l-4 border-[#CF0D1D] ml-8'
                        : 'bg-white border border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-semibold text-gray-700">
                        {msg.respondidoPorAdmin ? '📧 Resposta do Suporte' : '👤 Você'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatarData(msg.criadoEm)}
                      </span>
                    </div>
                    <p className="text-gray-800 whitespace-pre-wrap">{msg.mensagem}</p>
                    {msg.screenshotUrl && (
                      <div className="mt-3">
                        <img
                          src={`${backendUrl}${msg.screenshotUrl}`}
                          alt="Screenshot"
                          className="max-w-full max-h-60 rounded-lg border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity object-contain"
                          onClick={() => handleImageClick(`${backendUrl}${msg.screenshotUrl}`)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Send Message Form */}
        <Card>
          <CardHeader>
            <CardTitle>Enviar Nova Mensagem</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleEnviarMensagem} className="space-y-4">
              <div>
                <textarea
                  value={novaMensagem}
                  onChange={(e) => setNovaMensagem(e.target.value)}
                  placeholder="Digite sua mensagem ou dúvida..."
                  className="w-full min-h-[120px] p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#CF0D1D] focus:border-transparent resize-vertical"
                  disabled={enviando}
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Anexar Captura de Tela (Opcional)
                </label>
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={enviando}
                    />
                    <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors">
                      <ImageIcon className="h-4 w-4 text-gray-600" />
                      <span className="text-sm text-gray-700">Selecionar Imagem</span>
                    </div>
                  </label>
                  <span className="text-xs text-gray-500">PNG ou JPG, máximo 2MB</span>
                </div>

                {previewUrl && (
                  <div className="mt-3 relative inline-block">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="max-w-xs rounded border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={enviando || !novaMensagem.trim()}
                  className="bg-[#CF0D1D] hover:bg-[#a00a17]"
                >
                  {enviando ? (
                    'Enviando...'
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Enviar Mensagem
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Image Modal */}
      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Captura de Tela</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            <img
              src={selectedImage}
              alt="Screenshot ampliado"
              className="max-w-full max-h-[70vh] object-contain"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsuarioSuporte;
